import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface NPSScore {
  id: string
  segment: string
  score: number
  promoters: number
  passives: number
  detractors: number
  responses: number
  trend: string
}

interface Survey {
  id: string
  name: string
  sent: number
  completed: number
  nps_score: number
  date: string
  status: string
}

interface Benchmark {
  id: string
  category: string
  industry_avg: number
  our_score: number
  percentile: number
}

interface Statistics {
  overall_nps: number
  total_responses: number
  response_rate: number
  industry_percentile: number
}

export default function NPSTrackingPage() {
  const [scores, setScores] = useState<NPSScore[]>([])
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [scoresRes, surveysRes, benchmarksRes, statsRes] = await Promise.all([
        fetch('/api/npstracking/scores'),
        fetch('/api/npstracking/surveys'),
        fetch('/api/npstracking/benchmarks'),
        fetch('/api/npstracking/statistics')
      ])
      if (scoresRes.ok) setScores(await scoresRes.json())
      if (surveysRes.ok) setSurveys(await surveysRes.json())
      if (benchmarksRes.ok) setBenchmarks(await benchmarksRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching NPS data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getNPSColor = (score: number) => {
    if (score >= 50) return 'text-green-600'
    if (score >= 0) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getTrendText = (trend: string) => {
    if (trend === 'up') return '↑ Improving'
    if (trend === 'down') return '↓ Declining'
    return '→ Stable'
  }

  const getTrendColor = (trend: string) => {
    if (trend === 'up') return 'text-green-600'
    if (trend === 'down') return 'text-red-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">NPS Tracking</h1>
          <p className="mt-2 text-gray-600">Phase 223 - Net Promoter Score monitoring and analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Overall NPS</p>
              <p className={`text-3xl font-bold ${getNPSColor(statistics.overall_nps)}`}>
                {statistics.overall_nps}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Responses</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_responses.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Response Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.response_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Industry Rank</p>
              <p className="text-2xl font-bold text-purple-600">Top {statistics.industry_percentile}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">NPS by Segment</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">NPS Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Promoters</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passives</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detractors</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responses</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scores.map((score) => (
                  <tr key={score.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{score.segment}</td>
                    <td className={`px-6 py-4 font-bold ${getNPSColor(score.score)}`}>{score.score}</td>
                    <td className="px-6 py-4 text-green-600">{score.promoters}%</td>
                    <td className="px-6 py-4 text-yellow-600">{score.passives}%</td>
                    <td className="px-6 py-4 text-red-600">{score.detractors}%</td>
                    <td className="px-6 py-4 text-gray-500">{score.responses.toLocaleString()}</td>
                    <td className={`px-6 py-4 ${getTrendColor(score.trend)}`}>{getTrendText(score.trend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Recent Surveys</h2>
            </div>
            <div className="p-6 space-y-4">
              {surveys.map((survey) => (
                <div key={survey.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{survey.name}</span>
                    <span className={`px-2 py-1 rounded text-sm ${survey.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {survey.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{survey.completed}/{survey.sent} responses</span>
                    <span className={`font-medium ${getNPSColor(survey.nps_score)}`}>NPS: {survey.nps_score}</span>
                    <span className="text-gray-500">{survey.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Industry Benchmarks</h2>
            </div>
            <div className="p-6 space-y-4">
              {benchmarks.map((bench) => (
                <div key={bench.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{bench.category}</span>
                    <span className="text-purple-600">Top {bench.percentile}%</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Industry Avg: {bench.industry_avg}</span>
                    <span className={bench.our_score > bench.industry_avg ? 'text-green-600' : 'text-red-600'}>
                      Our Score: {bench.our_score}
                    </span>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full">
                    <div
                      className={`absolute h-2 rounded-full ${bench.our_score > bench.industry_avg ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(100, (bench.our_score + 100) / 2)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
